"use client";

import type { Variants } from "motion/react";
import type { HTMLAttributes } from "react";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { motion, useAnimation } from "motion/react";

interface LinkIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface LinkIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  loop?: boolean;
  animateOnMount?: boolean;
}

const pathVariants = (loop: boolean): Variants => ({
  initial: { pathLength: 1, pathOffset: 0, rotate: 0 },
  animate: {
    pathLength: [1, 0.97, 1, 0.97, 1],
    pathOffset: [0, 0.05, 0, 0.05, 0],
    rotate: [0, -5, 0],
    transition: {
      rotate: {
        duration: 0.5,
      },
      duration: 1,
      times: [0, 0.2, 0.4, 0.6, 1],
      ease: "easeInOut",
      repeat: loop ? Number.POSITIVE_INFINITY : 0,
      repeatType: loop ? "loop" : undefined,
      repeatDelay: loop ? 1 : 0,
    },
  },
});

const LinkIcon = forwardRef<LinkIconHandle, LinkIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 28,
      loop = false,
      animateOnMount = false,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => void controls.start("animate"),
        stopAnimation: () => void controls.start("normal"),
      };
    });

    useEffect(() => {
      if ((loop || animateOnMount) && !isControlledRef.current) {
        void controls.start("animate");
      }
    }, [loop, animateOnMount, controls]);

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("animate");
        } else {
          onMouseEnter?.(e);
        }
      },
      [controls, onMouseEnter, loop],
    );

    const handleMouseLeave = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("normal");
        } else {
          onMouseLeave?.(e);
        }
      },
      [controls, onMouseLeave, loop],
    );
    return (
      <div
        className={twMerge(className)}
        onMouseEnter={(e) => void handleMouseEnter(e)}
        onMouseLeave={(e) => void handleMouseLeave(e)}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            variants={pathVariants(loop)}
            animate={controls}
          />
          <motion.path
            d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            variants={pathVariants(loop)}
            animate={controls}
          />
        </svg>
      </div>
    );
  },
);

LinkIcon.displayName = "LinkIcon";

export { LinkIcon };
